import type { GraphEdgeSettingsStyle, ProtocolEdgeType } from '@ipfabric/utils';
import { moveEdgeSettings } from 'components/Graph/commands/moveEdgeSettings';
import { setDraggedProtocol } from 'components/Graph/commands/setDraggedProtocol';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import type { RefObject } from 'react';
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { canSort } from 'utils/dnd/canSort';
import { useEmptyPreview } from 'utils/dnd/useEmptyPreview';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';

interface Props {
  dispatch: CommandDispatcher<GraphCommandContext>;
  disableDrag: boolean;
  presetEditMode: boolean;
  groupIndex: number;
  protocolIndex: number;
  protocolName?: ProtocolEdgeType;
  rowRef: RefObject<HTMLDivElement>;
  style?: GraphEdgeSettingsStyle;
  title: string;
  rowIsGroupHeading: boolean;
}

export interface DragProtocolItem {
  groupIndex: number;
  originalGroupIndex: number;
  protocolIndex?: number;
  protocolName?: ProtocolEdgeType;
  style?: GraphEdgeSettingsStyle;
  title: string;
  type: 'edge' | 'group';
}

export function useProtocolDragAndDrop(props: Props): void {
  const [, drag, preview] = useDrag({
    item: {
      groupIndex: props.groupIndex,
      originalGroupIndex: props.groupIndex,
      protocolIndex: props.protocolIndex,
      protocolName: props.protocolName,
      style: props.style,
      title: props.title,
      type: 'edge',
    },
    canDrag: () => {
      return (
        props.presetEditMode && !props.rowIsGroupHeading && !props.disableDrag
      );
    },
    begin: () => {
      // We want to display white box with border. react-dnd creates drag image immeditaly.
      // We need to delay displaying white box before image is captured.
      setTimeout(() => {
        if (props.protocolName) {
          props.dispatch(setDraggedProtocol, { protocol: props.protocolName });
        }
      }, 0);
    },
    end: () => {
      props.dispatch(setDraggedProtocol, { protocol: null });
    },
  });

  const [, drop] = useDrop({
    accept: 'edge',
    hover: (item: DragProtocolItem, monitor: DropTargetMonitor) => {
      if (!props.rowRef.current) {
        return;
      }
      const dragGroupIndex = item.groupIndex;
      const hoverGroupIndex = props.groupIndex;
      const dragProtocolIndex = item.protocolIndex!;
      const hoverProtocolIndex = props.protocolIndex;

      if (item.type === 'group') {
        return;
      }

      // Don't replace items with themselves
      if (
        (dragGroupIndex === hoverGroupIndex &&
          dragProtocolIndex === hoverProtocolIndex) ||
        hoverProtocolIndex === -1
      ) {
        return;
      }

      if (
        canSort(
          props.rowRef.current,
          monitor,
          dragProtocolIndex,
          hoverProtocolIndex,
        )
      ) {
        props.dispatch(moveEdgeSettings, {
          dragGroupIndex,
          dragProtocolIndex,
          hoverGroupIndex,
          hoverProtocolIndex,
        });

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.

        // Example: https://github.com/react-dnd/react-dnd/blob/09838c9c203554bfe23c97f7af82b120f7f28be8/packages/documentation/examples-hooks/src/04-sortable/simple/Card.tsx
        item.protocolIndex = hoverProtocolIndex;
        item.groupIndex = hoverGroupIndex;
      }
    },
  });

  drag(drop(props.rowRef));

  useEmptyPreview(preview);
}
